export const RESPONSIVE_RESOURCE_SLOT_SIZE = 415;
export const RESPONSIVE_LENS_PROCESS_WIDTH = 340;
export const RESPONSIVE_LENS_RESOURCE_GAP = 16;
export const WIDE_LENS_RESOURCE_SLOT_SIZE = 423;
export const IMAGE_NAVIGATION_CARD_MIN_WIDTH = 100;
export const IMAGE_NAVIGATION_CARD_MAX_WIDTH = 196;
export const IMAGE_NAVIGATION_CARD_GAP = 8;
export const TEXT_NAVIGATION_COLUMN_WIDTH = 120;
export const TEXT_NAVIGATION_COLUMN_GAP = 24;

interface ResolveTextNavigationColumnCountParams {
  contentHeight: number;
  availableHeight: number;
}

export const resolveTextNavigationColumnCount = ({
  contentHeight,
  availableHeight,
}: ResolveTextNavigationColumnCountParams) => {
  if (contentHeight <= 0 || availableHeight <= 0) return 1;

  return Math.max(1, Math.ceil(contentHeight / availableHeight));
};

interface ResolveRenderedTextNavigationColumnCountParams {
  currentColumnCount: number;
  scrollWidth: number;
  columnWidth?: number;
  columnGap?: number;
}

export const resolveRenderedTextNavigationColumnCount = ({
  currentColumnCount,
  scrollWidth,
  columnWidth = TEXT_NAVIGATION_COLUMN_WIDTH,
  columnGap = TEXT_NAVIGATION_COLUMN_GAP,
}: ResolveRenderedTextNavigationColumnCountParams) => {
  if (scrollWidth <= 0 || columnWidth <= 0 || columnGap < 0) return currentColumnCount;

  const renderedColumnCount = Math.max(
    1,
    Math.round((scrollWidth + columnGap) / (columnWidth + columnGap)),
  );

  return Math.max(currentColumnCount, renderedColumnCount);
};

export const getImageNavigationPreferredWidth = (imageItemCount: number) => {
  return (
    imageItemCount * IMAGE_NAVIGATION_CARD_MAX_WIDTH +
    Math.max(0, imageItemCount - 1) * IMAGE_NAVIGATION_CARD_GAP
  );
};

interface CanDisplayTextImageNavigationParams {
  availableWidth: number;
  textNavigationWidth: number;
  textNavigationCount: number;
  imageNavigationCount: number;
  moduleGap: number;
}

export const canDisplayTextImageNavigation = ({
  availableWidth,
  textNavigationWidth,
  textNavigationCount,
  imageNavigationCount,
  moduleGap,
}: CanDisplayTextImageNavigationParams) => {
  if (imageNavigationCount === 0) return false;

  const moduleCount = textNavigationCount + imageNavigationCount;
  const totalModuleGap = Math.max(0, moduleCount - 1) * moduleGap;
  const minimumImageNavigationWidth = imageNavigationCount * IMAGE_NAVIGATION_CARD_MIN_WIDTH;

  return textNavigationWidth + totalModuleGap + minimumImageNavigationWidth <= availableWidth;
};

interface CanDisplayPriorityModuleParams {
  availableWidth: number;
  higherPriorityWidth: number;
  moduleWidth: number;
}

export const canDisplayPriorityModule = ({
  availableWidth,
  higherPriorityWidth,
  moduleWidth,
}: CanDisplayPriorityModuleParams) => {
  return higherPriorityWidth + moduleWidth <= availableWidth;
};

interface CanDisplayResourceSlotsParams {
  availableWidth: number;
  higherPriorityWidth: number;
  resourceSlotCount: number;
  resourceSlotSize?: number;
}

export const canDisplayResourceSlots = ({
  availableWidth,
  higherPriorityWidth,
  resourceSlotCount,
  resourceSlotSize = RESPONSIVE_RESOURCE_SLOT_SIZE,
}: CanDisplayResourceSlotsParams) => {
  return canDisplayPriorityModule({
    availableWidth,
    higherPriorityWidth,
    moduleWidth: resourceSlotCount * resourceSlotSize,
  });
};

interface ResolveTextLensResourceVisibilityParams {
  availableWidth: number;
  textNavigationWidth: number;
  hasLensProcess: boolean;
  resourceSlotCount: number;
  resourceSlotSize?: number;
}

export interface TextLensResourceVisibility {
  showLensProcess: boolean;
  visibleResourceSlotCount: number;
}

export const resolveTextLensResourceVisibility = ({
  availableWidth,
  textNavigationWidth,
  hasLensProcess,
  resourceSlotCount,
  resourceSlotSize = RESPONSIVE_RESOURCE_SLOT_SIZE,
}: ResolveTextLensResourceVisibilityParams): TextLensResourceVisibility => {
  const showLensProcess =
    hasLensProcess &&
    canDisplayPriorityModule({
      availableWidth,
      higherPriorityWidth: textNavigationWidth,
      moduleWidth: RESPONSIVE_LENS_PROCESS_WIDTH,
    });
  const lensProcessWidth = showLensProcess ? RESPONSIVE_LENS_PROCESS_WIDTH : 0;
  const lensResourceGap =
    showLensProcess && resourceSlotCount > 0 ? RESPONSIVE_LENS_RESOURCE_GAP : 0;
  const resourceSlotAvailableWidth = Math.max(
    0,
    availableWidth - textNavigationWidth - lensProcessWidth - lensResourceGap,
  );

  return {
    showLensProcess,
    visibleResourceSlotCount: Math.min(
      resourceSlotCount,
      Math.floor(resourceSlotAvailableWidth / resourceSlotSize),
    ),
  };
};

interface ResolveVisibleResourceSlotCountParams {
  availableWidth: number;
  higherPriorityWidth: number;
  resourceSlotCount: number;
  resourceSlotSize?: number;
}

export const resolveVisibleResourceSlotCount = ({
  availableWidth,
  higherPriorityWidth,
  resourceSlotCount,
  resourceSlotSize = RESPONSIVE_RESOURCE_SLOT_SIZE,
}: ResolveVisibleResourceSlotCountParams) => {
  const resourceSlotAvailableWidth = Math.max(0, availableWidth - higherPriorityWidth);

  return Math.min(resourceSlotCount, Math.floor(resourceSlotAvailableWidth / resourceSlotSize));
};
